import * as React from "react";
import { Grid, GridColumn as Column, GridNoRecords } from "@progress/kendo-react-grid";
import APIConnector from "../../../apiconnector";
import CommonVariables from "../../../variables";
import Notifications from "../../../Notifications/Messages";
import { Filter, Operators, TextFilter, DateFilter } from "@progress/kendo-react-data-tools";
import { filterBy, orderBy } from "@progress/kendo-data-query";
import RefreshIcon from '@mui/icons-material/Refresh';
import { GridPDFExport } from "@progress/kendo-react-pdf";
import { ExcelExport } from "@progress/kendo-react-excel-export";
import PrintIcon from '@mui/icons-material/Print';
import EditIcon from '@mui/icons-material/Edit';
import { Dialog } from "@progress/kendo-react-dialogs";
import { Button } from "@progress/kendo-react-buttons";
import { Input, TextArea } from "@progress/kendo-react-inputs";
import { TableChart } from "@mui/icons-material";
import { formatDateOnly } from '../../../commonfunctions';
/**
 * EmailJobs Component enables users to view email job records and open a selected record in read-only mode.
 * This page intentionally does not create, update, or delete records.
 */
const WorkflowEmails = (props) => {
    const initialFilter = {
        logic: "and",
        filters: [{
            field: "subject",
            operator: "contains",
            value: "",
        }]
    };
    const emailJobFields = [
        {
            name: "deadlineNotificationId",
            label: "Deadline Notification Id",
            filter: TextFilter,
            operators: Operators.text,
            width: "260"
        },
        {
            name: "notificationType",
            label: "Notification Type",
            filter: TextFilter,
            operators: Operators.text,
            width: "190"
        },
        {
            name: "subject",
            label: "Subject",
            filter: TextFilter,
            operators: Operators.text,
            width: "260"
        },
        {
            name: "notificationSent",
            label: "Notification Sent",
            filter: TextFilter,
            operators: Operators.text,
            width: "170"
        },
        {
            name: "emailTo",
            label: "Email To",
            filter: TextFilter,
            operators: Operators.text,
            width: "240"
        },
        {
            name: "emailCC",
            label: "Email CC",
            filter: TextFilter,
            operators: Operators.text,
            width: "240"
        },
        {
            name: "createdOn",
            label: "Created On",
            filter: DateFilter,
            operators: Operators.date,
            width: "160"
        },
        {
            name: "modifiedOn",
            label: "Modified On",
            filter: DateFilter,
            operators: Operators.date,
            width: "160"
        }
    ];
    const gridFields = emailJobFields
        .filter((field) => field.name)
        .map(({ width, ...field }) => field);
    const [filter, setFilter] = React.useState(initialFilter);
    const [sort, setSort] = React.useState([]);
    const [data, setData] = React.useState([]);
    const [showEmailJobDialog, setShowEmailJobDialog] = React.useState(false);
    const [selectedEmailJob, setSelectedEmailJob] = React.useState(null);
    const toolbarButtonBorderColor = "#8b96c7";
    const toolbarButtonHoverBorderColor = "#574ea8";
    const toolbarButtonStyle = {
        marginLeft: "15px",
        marginBottom: "3px",
        font: "caption",
        display: "inline-flex",
        alignItems: "center",
        gap: "4px",
        border: `1px solid ${toolbarButtonBorderColor}`,
        borderRadius: "4px",
        padding: "6px 8px",
        backgroundColor: "#ebefff",
        cursor: "pointer",
        lineHeight: "20px",
        verticalAlign: "middle"
    };
    const toolbarIconStyle = {
        display: "block",
        borderColor: "transparent",
        color: "#5454ec",
        cursor: "pointer",
        fontSize: "18px",
        width: "18px",
        height: "18px"
    };
    const handleToolbarButtonMouseEnter = (event) => {
        event.currentTarget.style.borderColor = toolbarButtonHoverBorderColor;
    };
    const handleToolbarButtonMouseLeave = (event) => {
        event.currentTarget.style.borderColor = toolbarButtonBorderColor;
    };
    const normalizeArrayResponse = (response) => {
        if (!response) {
            return [];
        }
        if (Array.isArray(response)) {
            return response;
        }
        const nestedData = response.data
            || response.Data
            || response.result
            || response.Result
            || response.items
            || response.Items
            || response.value
            || response.Value;
        if (Array.isArray(nestedData)) {
            return nestedData;
        }
        if (nestedData && typeof nestedData === "object") {
            return [nestedData];
        }
        return [response];
    };
    const normalizeSingleResponseItem = (response) => {
        if (!response) {
            return null;
        }
        if (Array.isArray(response)) {
            return response[0] || null;
        }
        const nestedData = response.data
            || response.Data
            || response.result
            || response.Result
            || response.items
            || response.Items
            || response.value
            || response.Value;
        if (Array.isArray(nestedData)) {
            return nestedData[0] || null;
        }
        if (nestedData && typeof nestedData === "object") {
            return nestedData;
        }
        return response;
    };
    const getEmailJobId = (emailJobItem) => {
        return emailJobItem?.deadlineNotificationId || "";
    };
    const mapEmailJobResponse = (response) => {
        return normalizeArrayResponse(response).map((item) => ({
            deadlineNotificationId: item.deadlineNotificationId,
            notificationType: item.notificationType,
            salesCoordinatorName: item.salesCoordinatorName,
            salesCoordinatorEmailAddress: item.salesCoordinatorEmailAddress,
            salesCoordinatorEmailSignature: item.salesCoordinatorEmailSignature,
            primaryContactEmailAddress: item.primaryContactEmailAddress,
            subject: item.subject,
            contactFirstname: item.contactFirstname,
            emailBody: item.emailBody,
            orderDueDate: item.orderDueDate,
            deliveryWeek: item.deliveryWeek,
            emailTo: item.emailTo,
            emailCC: item.emailCC,
            notificationSent: item.notificationSent,
            createdOn: formatDateOnly(item.createdOn),
            modifiedOn: formatDateOnly(item.modifiedOn)
        }));
    };
    React.useEffect(() => {
        refreshEmailJobs();
    }, []);
    const refreshEmailJobs = (msg) => {
        props.onLoaderRaise(true, "Loading email jobs ...", { marginTop: "145px" });
        APIConnector.Get(CommonVariables.urls.GetAllWorkflowEmails).then(function (response) {
            props.onLoaderRaise(false);
            if (!CommonVariables.isError(response)) {
                return;
            }
            setData(mapEmailJobResponse(response));
            if (msg) {
                Notifications.ShowSuccessMessage(msg);
            }
        });
    };
    const openEmailJobDialog = (item) => {
        const emailJobId = getEmailJobId(item);
        if (!emailJobId) {
            Notifications.ShowErrorMessage("Email Job Id was not found.");
            return;
        }
        const inputModel = {
            id: emailJobId
        };
        props.onLoaderRaise(true, "Loading email job ...", { marginTop: "145px" });
        APIConnector.Get(CommonVariables.urls.GetWorkflowEmailById, inputModel).then(function (response) {
            props.onLoaderRaise(false);
            if (!CommonVariables.isError(response)) {
                return;
            }
            const emailJob = normalizeSingleResponseItem(response);
            if (!emailJob) {
                Notifications.ShowWarningMessage("Email Job details were not found.");
                return;
            }
            setSelectedEmailJob(mapEmailJobResponse([emailJob])[0]);
            setShowEmailJobDialog(true);
        });
    };
    const closeEmailJobDialog = () => {
        setShowEmailJobDialog(false);
        setSelectedEmailJob(null);
    };
    let gridPDFExport;
    const exportPDF = () => {
        if (gridPDFExport) {
            gridPDFExport.save(data);
        }
    };
    let _export;
    const exportExcel = () => {
        _export.save(data);
    };
    const MyViewCommandCell = (props) => (
        <ViewCommandCell {...props} openEmailJobDialog={openEmailJobDialog} />
    );
    const ViewCommandCell = (props) => {
        return (
            <td className="flex justify-evenly">
                <span title="View">
                    <EditIcon
                        onClick={() => props.openEmailJobDialog(props.dataItem)}
                        title="View"
                        style={{ "display": "inline-block", "borderColor": "transparent", "color": "#5454ec", "cursor": "pointer" }}
                    />
                </span>
            </td>
        );
    };
    const renderEmailJobColumns = () => emailJobFields.map((field) => (
        <Column
            key={field.name}
            field={field.name}
            width={field.width}
            title={field.label}
        />
    ));
    const gridData = orderBy(filterBy(data, filter), sort);
    const grid = (
        <Grid className="rounded-2xl border-b-4" style={{ height: (window.innerHeight - 317) + "px" }}
            data={gridData}
            sortable={{ allowUnsort: true, mode: "single" }}
            sort={sort}
            onSortChange={(e) => setSort(e.sort)}
            pageable={true}
            take={1000}
            total={gridData.length}
            resizable={true}
            selectable={{
                enabled: true,
                drag: false,
                cell: false,
                mode: "single"
            }}>
            <GridNoRecords>
                No Email Jobs available
            </GridNoRecords>
            {renderEmailJobColumns()}
            <Column cell={MyViewCommandCell} width="60" title="View" sortable={false} />
        </Grid>
    );
    return (
        <div className="PageStyle">
            <h2 className="inline-block pt-1 primary-heading">Email Jobs</h2>
            <div className="row" style={{ "marginRight": "auto" }}>
                <div className="col-sm-6">
                    <Filter
                        value={filter}
                        onChange={(e) => setFilter(e.filter)}
                        fields={gridFields}
                    />
                </div>
                <div className="col-sm-6" style={{ marginTop: "auto", marginRight: "auto", paddingRight: 0, textAlign: "end" }}>
                    <span
                        title="Print"
                        onClick={exportPDF}
                        onMouseEnter={handleToolbarButtonMouseEnter}
                        onMouseLeave={handleToolbarButtonMouseLeave}
                        style={toolbarButtonStyle}
                    >
                        <span>Print</span>
                        <PrintIcon style={toolbarIconStyle} />
                    </span>
                    <span
                        title="Excel"
                        onClick={exportExcel}
                        onMouseEnter={handleToolbarButtonMouseEnter}
                        onMouseLeave={handleToolbarButtonMouseLeave}
                        style={toolbarButtonStyle}
                    >
                        <span>Export to Excel</span>
                        <TableChart style={toolbarIconStyle} />
                    </span>
                    <span
                        title="Refresh grid"
                        onClick={refreshEmailJobs}
                        onMouseEnter={handleToolbarButtonMouseEnter}
                        onMouseLeave={handleToolbarButtonMouseLeave}
                        style={toolbarButtonStyle}
                    >
                        <span>Refresh</span>
                        <RefreshIcon style={toolbarIconStyle} />
                    </span>
                </div>
            </div>
            {grid}
            {showEmailJobDialog && selectedEmailJob && (
                <Dialog title="Email Job Details" onClose={closeEmailJobDialog} width={760}>
                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "14px 16px", padding: "8px 4px" }}>
                        <div>
                            <label style={{ display: "block", marginBottom: "4px" }}>Deadline Notification Id</label>
                            <Input disabled={true} value={selectedEmailJob.deadlineNotificationId || ""} />
                        </div>
                        <div>
                            <label style={{ display: "block", marginBottom: "4px" }}>Notification Type</label>
                            <Input disabled={true} value={selectedEmailJob.notificationType ?? ""} />
                        </div>
                        <div style={{ gridColumn: "1 / 3" }}>
                            <label style={{ display: "block", marginBottom: "4px" }}>Subject</label>
                            <Input disabled={true} value={selectedEmailJob.subject || ""} />
                        </div>
                        <div>
                            <label style={{ display: "block", marginBottom: "4px" }}>Notification Sent</label>
                            <Input disabled={true} value={selectedEmailJob.notificationSent !== undefined && selectedEmailJob.notificationSent !== null ? selectedEmailJob.notificationSent.toString() : ""} />
                        </div>
                        <div>
                            <label style={{ display: "block", marginBottom: "4px" }}>Created On</label>
                            <Input disabled={true} value={selectedEmailJob.createdOn || ""} />
                        </div>
                        <div>
                            <label style={{ display: "block", marginBottom: "4px" }}>Email To</label>
                            <Input disabled={true} value={selectedEmailJob.emailTo || ""} />
                        </div>
                        <div>
                            <label style={{ display: "block", marginBottom: "4px" }}>Email CC</label>
                            <Input disabled={true} value={selectedEmailJob.emailCC || ""} />
                        </div>
                        <div>
                            <label style={{ display: "block", marginBottom: "4px" }}>Modified On</label>
                            <Input disabled={true} value={selectedEmailJob.modifiedOn || ""} />
                        </div>
                        <div style={{ gridColumn: "1 / 3" }}>
                            <label style={{ display: "block", marginBottom: "4px" }}>Email Body</label>
                            <TextArea disabled={true} value={selectedEmailJob.emailBody || ""} rows={6} />
                        </div>
                    </div>
                    <div style={{ width: "20%", display: "flex", gap: "4px" }}>
                        <Button onClick={closeEmailJobDialog}>Close</Button>
                    </div>
                </Dialog>
            )}
            <GridPDFExport ref={(pdfExport) => (gridPDFExport = pdfExport)}>
                {renderEmailJobColumns()}
                {grid}
            </GridPDFExport>
            <ExcelExport data={data} ref={(exporter) => { _export = exporter; }}>
                {renderEmailJobColumns()}
            </ExcelExport>
        </div>
    );
};
export default WorkflowEmails;
